/**
 * Treffer der Portalsuche (`GET /protected/organizations/search`) als
 * auswählbare Beteiligten-Zeilen – genutzt im Fragebogen/Akte
 * (AddParticipantDialog) und in der Fallanlage (ContactPickerDialog).
 *
 * Kundenanforderung (Kanzlei Polz, 07.10.2026, AH Singer Buchloe): Bei der
 * Beteiligten-Auswahl muss man immer die Zentrale eines Unternehmens wählen
 * können – meist ist der konkrete Sachbearbeiter nicht bekannt. Je Unternehmen
 * steht deshalb eine Zeile „Zentrale“ an erster Stelle; die einzelnen
 * Abteilungen/Sachbearbeiter folgen darunter.
 *
 * Die Zentrale ist
 *  - die Abteilung „Zentrale“, falls das Unternehmen eine hat (wird wie jede
 *    Abteilung über PUT …/departments beteiligt), sonst
 *  - die zentrale E-Mail-Adresse des Unternehmens bzw. seines Hauptstandorts
 *    (wird als externer Beteiligter angelegt; das Backend verknüpft die
 *    Adresse mit der registrierten Organisation).
 */

import { PARTICIPANT_CATEGORIES, roleForContactType } from "./participantCategories";

export const CENTRAL_LABEL = "Zentrale";

export type PortalEntryKind = "central" | "department";

export interface PortalEntry {
  /** Eindeutiger Zeilenschlüssel (v-for, Auswahl) */
  key: string;
  /** department = Beteiligung über eine Abteilung; central = über die zentrale E-Mail */
  kind: PortalEntryKind;
  isCentral: boolean;
  departmentId: number | null;
  departmentName: string;
  departmentEmail: string;
  /** Zieladresse einer Zentrale ohne eigene Abteilung */
  centralEmail: string;
  locationName: string;
  locationEmail: string;
  /** PLZ und Ort des Standorts (Kundenfeedback 18.10.2026, XII: Ort im Suchergebnis) */
  locationCity?: string;
  companyName: string;
  companyEmail: string;
  organizationId: number | null;
  /** Unternehmensrollen der Organisation (tbl_organizations.roles) */
  organizationRoles: string[];
}

/** Auswahl eines Portal-Treffers als Beteiligter (z. B. in der Fallanlage). */
export interface PortalParticipantSelection {
  entry: PortalEntry;
  /** Fall-Rolle; aus den Unternehmensrollen vorbelegt, vom Anwender änderbar. */
  caseRole: string;
}

interface RawDepartment { id: number; name?: string | null; email?: string | null }
interface RawLocation {
  id: number;
  name?: string | null;
  email?: string | null;
  zip?: string | null;
  city?: string | null;
  departments?: RawDepartment[] | null;
}
interface RawCompany {
  id: number;
  idMainLocation?: number | null;
  name?: string | null;
  legalForm?: string | null;
  email?: string | null;
  locations?: RawLocation[] | null;
}
interface RawOrganization { id: number; roles?: string | string[] | null; companies?: RawCompany[] | null }

export function isCentralDepartmentName (name: string | null | undefined): boolean {
  return (name ?? "").trim().toLowerCase() === CENTRAL_LABEL.toLowerCase();
}

function companyDisplayName (company: RawCompany): string {
  const name = (company.name ?? "").trim();
  if (!name) { return "Unbenanntes Unternehmen"; }
  return company.legalForm ? `${name} ${company.legalForm}`.trim() : name;
}

/** Hauptstandort zuerst, damit dessen Daten die Zentrale beschreiben. */
function orderedLocations (company: RawCompany): RawLocation[] {
  const locations = Array.isArray(company.locations) ? company.locations : [];
  const main = locations.find((l) => l.id === company.idMainLocation);
  return main ? [main, ...locations.filter((l) => l !== main)] : locations;
}

function parseRoles (roles: RawOrganization["roles"]): string[] {
  if (Array.isArray(roles)) { return roles.filter((r) => typeof r === "string"); }
  if (!roles) { return []; }
  try {
    const parsed: unknown = JSON.parse(roles);
    return Array.isArray(parsed) ? parsed.filter((r): r is string => typeof r === "string") : [];
  } catch {
    return [];
  }
}

function baseEntry (org: RawOrganization, company: RawCompany, location: RawLocation | undefined) {
  return {
    locationName: location?.name || "Unbenannter Standort",
    locationEmail: location?.email || "",
    locationCity: [location?.zip, location?.city].map((v) => (v ?? "").trim()).filter(Boolean).join(" "),
    companyName: companyDisplayName(company),
    companyEmail: company.email || "",
    organizationId: org.id ?? null,
    organizationRoles: parseRoles(org.roles),
  };
}

function centralEntry (org: RawOrganization, company: RawCompany, locations: RawLocation[]): PortalEntry | null {
  for (const location of locations) {
    const dept = (location.departments ?? []).find((d) => isCentralDepartmentName(d.name));
    if (dept) {
      return {
        key: `dept-${dept.id}`,
        kind: "department",
        isCentral: true,
        departmentId: dept.id,
        departmentName: CENTRAL_LABEL,
        departmentEmail: dept.email || "",
        centralEmail: "",
        ...baseEntry(org, company, location),
      };
    }
  }
  const emailLocation = locations.find((l) => l.email);
  const email = company.email || emailLocation?.email || "";
  if (!email) { return null; }
  return {
    key: `central-${company.id}`,
    kind: "central",
    isCentral: true,
    departmentId: null,
    departmentName: CENTRAL_LABEL,
    departmentEmail: "",
    centralEmail: email,
    ...baseEntry(org, company, company.email ? locations[0] : emailLocation),
  };
}

export function flattenPortalOrganizations (organizations: unknown): PortalEntry[] {
  if (!Array.isArray(organizations)) { return []; }
  return (organizations as RawOrganization[]).flatMap((org) =>
    (org.companies ?? []).flatMap((company) => {
      const locations = orderedLocations(company);
      const central = centralEntry(org, company, locations);
      const departments: PortalEntry[] = locations.flatMap((location) =>
        (location.departments ?? [])
          .filter((d) => !(central?.departmentId === d.id))
          .map((d) => ({
            key: `dept-${d.id}`,
            kind: "department" as const,
            isCentral: false,
            departmentId: d.id,
            departmentName: d.name || "Unbenannte Abteilung",
            departmentEmail: d.email || "",
            centralEmail: "",
            ...baseEntry(org, company, location),
          })),
      );
      return central ? [central, ...departments] : departments;
    }),
  );
}

/** E-Mail-Adressen, über die eine Zeile mit eigenen Kontakten abgeglichen wird. */
export function portalEntryEmails (entry: PortalEntry): string[] {
  const own = entry.kind === "central" ? entry.centralEmail : entry.departmentEmail;
  return own ? [own.toLowerCase().trim()] : [];
}

/** Zieladresse für Benachrichtigung/Anzeige. */
export function portalEntryEmail (entry: PortalEntry): string {
  return entry.centralEmail || entry.departmentEmail || entry.locationEmail || entry.companyEmail;
}

export function portalEntryTitle (entry: PortalEntry): string {
  return `${entry.companyName} (${entry.departmentName})`;
}

/** Fall-Rolle, die für einen Portal-Treffer vorbelegt wird (änderbar). */
export function defaultRoleForPortalEntry (entry: PortalEntry): string {
  const hit = PARTICIPANT_CATEGORIES.find((c) => !c.catchAll && entry.organizationRoles.includes(c.role));
  return hit ? hit.role : roleForContactType(null);
}
